import { ROLES, type Continuity, type Zone } from '@dissidium/contracts';
import { validateLedger } from '../continuity/ledger.ts';

export type Prerequisites = Readonly<{
  all: readonly string[];
  any: readonly string[];
  none: readonly string[];
}>;

export type Effects = Readonly<{
  add: readonly string[];
  remove: readonly string[];
}>;

export type QuestNode = Readonly<{
  id: string;
  label: string;
  prerequisites: Prerequisites;
  effects: Effects;
}>;

export type QuestState = Readonly<{
  facts: readonly string[];
  completedNodeIds: readonly string[];
}>;

const text = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`${label} must be non-empty text`);
  return value;
};

function uniqueText(values: readonly string[], label: string): void {
  for (const value of values) text(value, label);
  if (new Set(values).size !== values.length) throw new Error(`${label} must not contain duplicates`);
}

const validatedPrerequisites = new WeakSet<object>();
const validatedEffects = new WeakSet<object>();
const validatedGraphs = new WeakSet<object>();
const factSets = new WeakMap<readonly string[], Set<string>>();

export function validatePrerequisites(value: Prerequisites): Prerequisites {
  // Authored prerequisites are immutable, so validate each object once.
  if (validatedPrerequisites.has(value)) return value;
  if (!value || !Array.isArray(value.all) || !Array.isArray(value.any) || !Array.isArray(value.none)) {
    throw new Error('Prerequisites need explicit all, any and none lists');
  }
  uniqueText(value.all, 'Required fact');
  uniqueText(value.any, 'Alternative required fact');
  uniqueText(value.none, 'Forbidden fact');
  validatedPrerequisites.add(value);
  return value;
}

export function prerequisitesMet(prerequisites: Prerequisites, facts: readonly string[]): boolean {
  validatePrerequisites(prerequisites);
  let known = factSets.get(facts);
  if (!known) {
    uniqueText(facts, 'Quest fact');
    known = new Set(facts);
    factSets.set(facts, known);
  }
  return prerequisites.all.every(fact => known.has(fact))
    && (prerequisites.any.length === 0 || prerequisites.any.some(fact => known.has(fact)))
    && prerequisites.none.every(fact => !known.has(fact));
}

export function validateEffects(value: Effects): Effects {
  if (validatedEffects.has(value)) return value;
  if (!value || !Array.isArray(value.add) || !Array.isArray(value.remove)) {
    throw new Error('Effects need explicit add and remove lists');
  }
  uniqueText(value.add, 'Added fact');
  uniqueText(value.remove, 'Removed fact');
  if (value.add.some(fact => value.remove.includes(fact))) throw new Error('One effect cannot add and remove the same fact');
  validatedEffects.add(value);
  return value;
}

export function makeQuestState(facts: readonly string[] = []): QuestState {
  uniqueText(facts, 'Quest fact');
  return { facts: [...facts].sort(), completedNodeIds: [] };
}

export function applyEffects(state: QuestState, effects: Effects): QuestState {
  validateEffects(effects);
  const facts = new Set(state.facts);
  for (const fact of effects.remove) facts.delete(fact);
  for (const fact of effects.add) facts.add(fact);
  return { facts: [...facts].sort(), completedNodeIds: [...state.completedNodeIds] };
}

function validateNode(node: QuestNode): void {
  text(node.id, 'Quest node id');
  text(node.label, 'Quest node label');
  validatePrerequisites(node.prerequisites);
  validateEffects(node.effects);
}

function validateGraph(nodes: readonly QuestNode[]): void {
  if (validatedGraphs.has(nodes)) return;
  for (const node of nodes) validateNode(node);
  if (new Set(nodes.map(node => node.id)).size !== nodes.length) throw new Error('Quest node ids must be unique');
  validatedGraphs.add(nodes);
}

export function availableQuestNodes(nodes: readonly QuestNode[], state: QuestState): QuestNode[] {
  validateGraph(nodes);
  return nodes.filter(node => !state.completedNodeIds.includes(node.id) && prerequisitesMet(node.prerequisites, state.facts));
}

export function applyQuestNode(nodes: readonly QuestNode[], state: QuestState, nodeId: string): QuestState {
  validateGraph(nodes);
  const node = nodes.find(candidate => candidate.id === nodeId);
  if (!node) throw new Error(`Unknown quest node ${nodeId}`);
  if (state.completedNodeIds.includes(node.id)) throw new Error(`Quest node ${node.id} is already complete`);
  if (!prerequisitesMet(node.prerequisites, state.facts)) throw new Error(`Quest prerequisites are not met for ${node.id}`);
  const effected = applyEffects(state, node.effects);
  return { facts: effected.facts, completedNodeIds: [...state.completedNodeIds, node.id] };
}

export type QuestRoleAlternative =
  | Readonly<{ kind: 'person'; personId: string; identifyingHistory: string; prerequisites: Prerequisites }>
  | Readonly<{ kind: 'evidence'; evidenceId: string; prerequisites: Prerequisites }>
  | Readonly<{ kind: 'revised-goal'; goalId: string; summary: string; prerequisites: Prerequisites }>
  | Readonly<{ kind: 'exclude'; reason: string; prerequisites: Prerequisites }>;

export type QuestRole = Readonly<{
  id: string;
  role: typeof ROLES[number];
  zone: Zone;
  preferredPersonId: string | null;
  alternatives: readonly QuestRoleAlternative[];
}>;

export type QuestRoleBinding =
  | Readonly<{ roleId: string; kind: 'person'; personId: string; substituted: boolean }>
  | Readonly<{ roleId: string; kind: 'evidence'; evidenceId: string }>
  | Readonly<{ roleId: string; kind: 'revised-goal'; goalId: string; summary: string }>
  | Readonly<{ roleId: string; kind: 'excluded'; reason: string }>;

function availableForRole(c: Continuity, personId: string, zone: Zone): boolean {
  const person = c.persons[personId];
  return Boolean(person && (person.lifecycle === 'active' || person.lifecycle === 'unmanifested') && (!person.location || person.location === zone));
}

/** Resolve an authored role without changing continuity or reviving its preferred identity. */
export function resolveQuestRole(input: Continuity, role: QuestRole, facts: readonly string[]): QuestRoleBinding {
  const c = validateLedger(input);
  text(role.id, 'Quest role id');
  if (!ROLES.includes(role.role)) throw new Error(`Unknown quest role ${role.role}`);
  uniqueText(facts, 'Quest fact');
  if (role.preferredPersonId && availableForRole(c, role.preferredPersonId, role.zone)) {
    return { roleId: role.id, kind: 'person', personId: role.preferredPersonId, substituted: false };
  }

  const preferred = role.preferredPersonId ? c.persons[role.preferredPersonId] : undefined;
  for (const alternative of role.alternatives) {
    validatePrerequisites(alternative.prerequisites);
    if (!prerequisitesMet(alternative.prerequisites, facts)) continue;
    if (alternative.kind === 'person') {
      text(alternative.identifyingHistory, 'Successor identifying history');
      if (alternative.personId === role.preferredPersonId || !availableForRole(c, alternative.personId, role.zone)) continue;
      const successor = c.persons[alternative.personId]!;
      if (preferred && successor.lineageKey === preferred.lineageKey) continue;
      return { roleId: role.id, kind: 'person', personId: alternative.personId, substituted: true };
    }
    if (alternative.kind === 'evidence') {
      return { roleId: role.id, kind: 'evidence', evidenceId: text(alternative.evidenceId, 'Alternative evidence id') };
    }
    if (alternative.kind === 'revised-goal') {
      return {
        roleId: role.id,
        kind: 'revised-goal',
        goalId: text(alternative.goalId, 'Revised goal id'),
        summary: text(alternative.summary, 'Revised goal summary'),
      };
    }
    return { roleId: role.id, kind: 'excluded', reason: text(alternative.reason, 'Role exclusion reason') };
  }
  throw new Error(`No valid authored alternative remains for quest role ${role.id}`);
}

export type PersonalGoal = Readonly<{
  id: string;
  personId: string;
  description: string;
  prerequisites: Prerequisites;
}>;

export type PersonalGoalOutcome = Readonly<{
  goalId: string;
  status: 'achieved' | 'compromised' | 'failed' | 'abandoned';
  summary: string;
  effects: Effects;
}>;

export type PersonalGoalResolution = Readonly<{
  continuity: Continuity;
  quest: QuestState;
  outcome: PersonalGoalOutcome;
}>;

/** Settle a personal goal independently of the main quest and never rewrite it later. */
export function resolvePersonalGoal(input: Continuity, state: QuestState, goal: PersonalGoal, outcome: PersonalGoalOutcome): PersonalGoalResolution {
  const c = validateLedger(input);
  text(goal.id, 'Personal goal id');
  text(goal.description, 'Personal goal description');
  validatePrerequisites(goal.prerequisites);
  if (!prerequisitesMet(goal.prerequisites, state.facts)) throw new Error(`Personal goal prerequisites are not met for ${goal.id}`);
  if (outcome.goalId !== goal.id) throw new Error('Personal goal outcome does not match its goal');
  text(outcome.summary, 'Personal goal outcome summary');
  validateEffects(outcome.effects);
  const person = c.persons[goal.personId];
  if (!person) throw new Error('Personal goal owner is unknown');
  if (person.current.personalOutcome !== 'Unresolved') throw new Error('A settled personal goal outcome cannot be overwritten');
  if (person.lifecycle === 'dead' && outcome.status !== 'failed' && outcome.status !== 'abandoned') {
    throw new Error('A dead player cannot receive a new posthumous personal success');
  }
  const continuity = structuredClone(c);
  continuity.persons[goal.personId]!.current.personalOutcome = `${outcome.status}: ${outcome.summary}`;
  return { continuity: validateLedger(continuity), quest: applyEffects(state, outcome.effects), outcome: structuredClone(outcome) };
}
